import React, { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { merchantLogin, getMerchantToken } from "../services/api";
import { useMerchantStore } from "../store/useMerchantStore";
import { AuthShell } from "../components/AuthShell";
import { friendlyError } from "../utils/friendlyError";

import { t } from "../i18n";
const labelStyle = { display: "block", marginBottom: 4, fontSize: 13, color: "#1b1530", fontWeight: 600 };
const inputStyle = {
  width: "100%",
  padding: "12px 14px",
  backgroundColor: "#ffffff",
  border: "1px solid #e6e1f0",
  color: "#1b1530",
  borderRadius: 12,
};

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const fromRegister = location.state?.registeredEmail;
  const setSession = useMerchantStore((s) => s.setSession);

  const [email, setEmail] = useState(fromRegister || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = `${t("login.title_short")} · ${t("title.suffix")}`;
  }, []);

  if (getMerchantToken()) {
    return <Navigate to="/" replace />;
  }

  async function submit(e) {
    e.preventDefault();
    setError("");
    const em = email.trim().toLowerCase();
    if (!em) {
      setError(t("login.email_required"));
      return;
    }
    if (!password) {
      setError(t("login.password_required"));
      return;
    }
    setSubmitting(true);
    try {
      const res = await merchantLogin({ contactEmail: em, password });
      if (!res?.token || !res?.merchant?.id) {
        throw new Error(t("login.unexpected_response"));
      }
      setSession({
        merchantId: res.merchant.id,
        merchantName: res.merchant.name,
      });
      navigate("/", { replace: true });
    } catch (err) {
      setError(friendlyError(err, t("login.failed")));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthShell
      eyebrow={t("login.eyebrow")}
      title={t("login.title")}
      lead={t("login.lead")}
    >
        <form onSubmit={submit}>
          <label style={labelStyle}>
            {t("login.email_label")}
            <input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("login.email_placeholder")}
              style={{ ...inputStyle, marginTop: 6 }}
            />
          </label>
          <label style={{ ...labelStyle, marginTop: 16 }}>
            {t("login.password_label")}
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("login.password_placeholder")}
              style={{ ...inputStyle, marginTop: 6 }}
            />
          </label>
          <div style={{ marginTop: 10, textAlign: "right" }}>
            <Link to="/forgot-password" style={{ color: "#6b4eaa", fontWeight: 600, fontSize: 13 }}>
              {t("login.forgot")}
            </Link>
          </div>
          {error ? (
            <div
              style={{
                marginTop: 16,
                padding: "10px 12px",
                backgroundColor: "#fbefec",
                color: "#a63a3a",
                borderRadius: 12,
                border: "1px solid #e3bdb6",
                fontSize: 14,
              }}
            >
              {error}
            </div>
          ) : null}
          <button
            type="submit"
            className="primary"
            disabled={submitting}
            style={{
              marginTop: 20,
              width: "100%",
              padding: "12px 16px",
              color: "#ffffff",
              border: "none",
              borderRadius: 12,
              cursor: submitting ? "not-allowed" : "pointer",
              fontSize: 15,
              fontWeight: 500,
              opacity: submitting ? 0.7 : 1,
            }}
          >
            {submitting ? t("login.signing_in") : t("login.sign_in")}
          </button>
        </form>
        <p style={{ marginTop: 24, fontSize: 14, color: "#6a6480", textAlign: "center" }}>
          {t("login.no_account")}{" "}
          <Link to="/register" style={{ color: "#6b4eaa", fontWeight: 600 }}>
            {t("login.register")}
          </Link>
        </p>
        <p style={{ marginTop: 10, fontSize: 13, color: "#8c8595", textAlign: "center" }}>
          <Link to="/admin/login" style={{ color: "#6b4eaa", fontWeight: 600 }}>
            {t("login.admin_link")}
          </Link>
        </p>
    </AuthShell>
  );
}
